import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { backendService, UserType } from '../services/backendService'
import { log } from '../utils/logManager'

export interface AuthContextType {
  isAuthenticated: boolean
  userType: UserType | null
  userId: string | null
  userName: string | null
  doctorId: string | null
  doctorName: string | null
  isAdmin: boolean
  login: (userId: string, userName: string, authToken: string, userType: UserType, isAdmin?: boolean) => void
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userType, setUserType] = useState<UserType | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [userName, setUserName] = useState<string | null>(null)
  const [doctorId, setDoctorId] = useState<string | null>(null)
  const [doctorName, setDoctorName] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [webSocket, setWebSocket] = useState<WebSocket | null>(null)

  const clearAuthState = () => {
    setIsAuthenticated(false)
    setUserType(null)
    setUserId(null)
    setUserName(null)
    setDoctorId(null)
    setDoctorName(null)
    setIsAdmin(false)
    localStorage.removeItem('isAdmin')
  }

  useEffect(() => {
    // Clean up any stale isAdmin flag from localStorage — admin status must be
    // re-derived from a verified backend response, never trusted from storage.
    localStorage.removeItem('isAdmin')
    // Clean up legacy authToken from localStorage (#99)
    localStorage.removeItem('authToken')

    // Check for stored user info on app start.
    // The JWT is now stored in an httpOnly cookie by the backend (#99) —
    // we never read it from localStorage. On page reload, the cookie is
    // automatically sent with requests; we verify the session via the
    // backend /api/auth/verify endpoint.
    const storedUserId = localStorage.getItem('userId') || localStorage.getItem('doctorId')
    const storedUserName = localStorage.getItem('userName') || localStorage.getItem('doctorName')
    const storedUserType = (localStorage.getItem('userType') as UserType | null) || 'doctor'

    if (storedUserId && storedUserName) {
      log.auth('info', 'Attempting session restore via cookie', { userType: storedUserType })

      if (storedUserType === 'farm') {
        // Farm: no verify endpoint, rely on cookie.
        setIsAuthenticated(true)
        setUserType('farm')
        setUserId(storedUserId)
        setUserName(storedUserName)
        setDoctorId(null)
        setDoctorName(null)
        setIsAdmin(false)
        localStorage.setItem('userType', 'farm')
        localStorage.setItem('userId', storedUserId)
        localStorage.setItem('userName', storedUserName)
        setLoading(false)
        return
      }

      // Default to non-admin while verification is in flight; only the backend
      // response can promote the session to admin.
      setIsAdmin(false)

      backendService.verifyToken()
        .then((response) => {
          setIsAuthenticated(true)
          setUserType('doctor')
          setUserId(response.doctorId)
          setUserName(response.doctorName)
          setDoctorId(response.doctorId)
          setDoctorName(response.doctorName)
          const adminStatus = response.isAdmin === true
          setIsAdmin(adminStatus)

          try {
            const ws = backendService.connectWebSocket()
            setWebSocket(ws)
            log.ws('info', 'WebSocket initiated (session restore)')
          } catch (error) {
            log.ws('error', 'WebSocket failed on restore', { error: String(error) })
          }

          log.auth('info', 'Session restored', { isAdmin: adminStatus })
        })
        .catch((error) => {
          log.auth('error', 'Session restore failed', { error: error.message })
          logout()
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      log.auth('debug', 'No stored user info found')
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = (userId: string, userName: string, authToken: string, userType: UserType, isAdmin: boolean = false) => {
    setIsAuthenticated(true)
    setUserType(userType)
    setUserId(userId)
    setUserName(userName)
    setDoctorId(userType === 'doctor' ? userId : null)
    setDoctorName(userType === 'doctor' ? userName : null)
    setIsAdmin(isAdmin)

    // Store non-sensitive user info for display on reload.
    // The JWT token is NOT stored in localStorage — it's set as an httpOnly
    // cookie by the backend (#99). Admin status is intentionally NOT persisted
    // — it must always be re-derived from /verify on reload.
    localStorage.setItem('userType', userType)
    localStorage.setItem('userId', userId)
    localStorage.setItem('userName', userName)

    // authToken is stored in memory only, not in localStorage (#99)
    backendService.setAuth(userId, authToken)

    if (userType === 'doctor') {
      try {
        const ws = backendService.connectWebSocket()
        setWebSocket(ws)
        log.ws('info', 'WebSocket initiated')
      } catch (error) {
        log.ws('error', 'WebSocket failed', { error: String(error) })
      }
    } else {
      setWebSocket(null)
    }

    log.auth('info', 'Login', { userType, isAdmin })
  }

  const logout = async () => {
    try {
      // Close WebSocket connection
      if (webSocket) {
        webSocket.close()
        setWebSocket(null)
        log.ws('info', 'WebSocket closed (logout)')
      }

      if (userType === 'doctor') {
        await backendService.logout()
      } else {
        backendService.clearAuth()
      }
    } catch (error) {
      log.auth('error', 'Backend logout failed', { error: String(error) })
    }

    clearAuthState()
    log.auth('info', 'Logged out')
  }

  const value: AuthContextType = {
    isAuthenticated,
    userType,
    userId,
    userName,
    doctorId,
    doctorName,
    isAdmin,
    login,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
